import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Asset, AssetGroupCount, AssetStackGroup } from '~/types/asset'
import { useApi } from '~/api'
import type { AssetFilters } from '~/types/filters'
import type { PaginatedResult, PaginationData } from '~/types/pagination'
import type { NetworkStatusResponse } from '~/types/map-assets.types'
import type { Group } from '~/types/group'

export const useAssetStore = defineStore('assetStore', (): AssetStore => {
  // State

  const searchTerm = ref('')
  const isLoading = ref<boolean>(false)
  const loadedTypes = ref<string[]>([])
  const assets = ref<Asset[]>([]) // Note: property for paginated / filtered views
  const assetsById = ref<Record<number, Asset>>({})
  const assetsCount = ref<AssetGroupCount[]>([])
  const focusedAssetId = ref<number | null>(null)
  const selectedAssetIds = ref<number[]>([])
  const networkStatus = ref<NetworkStatusResponse>({
    assetsWithActiveAlertsCount: 0,
    communicatingAssetsCount: 0,
    communicationLostAssetsCount: 0,
    neverCommunicatedAssetsCount: 0,
    onAssetsCount: 0,
    totalAssetCount: 0
  })
  const pagination = ref<PaginationData>({
    current_page: 1,
    per_page: 10,
    sort: 'Id asc',
    total: 0,
    total_pages: 1
  })
  const sectionRouteMap = shallowRef(new Map<number, number>()) // Map of section ID to route ID

  // To be removed in a next iteration, use FilterStore instead ; move page and pageSize to new pagination ref
  const currentFilters = ref<AssetFilters>({
    assetTypeIds: [],
    status: '',
    search: '',
    page: 1,
    pageSize: 10
  })
  const stacksGrouping = ref<AssetStackGroup>('site')

  const assetsIds: ComputedRef<number[]> = computed(() => assets.value.map((asset) => asset.id))

  // Actions
  const loadFilteredAssets = async (filters: AssetFilters): Promise<PaginatedResult<Asset>> => {
    isLoading.value = true

    try {
      const api = useApi()
      const response = await api.assets.getFilteredAssets(filters)
      assets.value = response.data
      pagination.value = response.pagination
      return response
    } finally {
      isLoading.value = false
    }
  }

  const loadAssetById = async (assetId: number): Promise<Asset | undefined> => {
    try {
      const api = useApi()
      const asset = await api.assets.getAssetById(assetId)
      appendToDictionary([asset])
      return asset
    } catch {
      return undefined
    }
  }

  const updateAsset = async (id: number, asset: Partial<Asset>): Promise<Asset> => {
    try {
      const api = useApi()
      const updatedAsset = await api.assets.updateAsset(id, asset)
      appendToDictionary([updatedAsset])
      return updatedAsset
    } catch (err) {
      throw new Error(`Failed to update asset with ID ${id}: ${err}`, { cause: err })
    }
  }

  const assignGroupsToAsset = async (assetId: number, groups: Group[]): Promise<void> => {
    try {
      const api = useApi()
      await api.assets.assignGroupsToAsset(
        assetId,
        groups.map((group) => group.id)
      )
      const asset = await getAssetById(assetId)
      if (!asset) {
        return
      }
      // We reassign from what we passed in, since the gateway does not send back the groups data.
      asset.groups = groups
    } catch (err) {
      throw new Error(`Failed to assign groups to asset with ID ${assetId}: ${err}`, { cause: err })
    }
  }

  const loadGroupedAssetsCount = async (groupBy: AssetStackGroup): Promise<void> => {
    isLoading.value = true

    try {
      const api = useApi()
      assetsCount.value = await api.assets.getGroupedAssetsCount(groupBy)
    } finally {
      isLoading.value = false
    }
  }

  const loadNetworkStatuses = async (filters: AssetFilters): Promise<NetworkStatusResponse> => {
    try {
      const api = useApi()
      const response = await api.assets.getAssetsNetworkStatuses(filters)
      networkStatus.value = response

      return response
    } catch (err) {
      console.error('Can not fetch network statuses', err)
      return Promise.reject('Can not fetch network statuses')
    }
  }

  const focusAsset = (assetId: number): void => {
    focusedAssetId.value = assetId
  }

  const addAssetToSelection = (asset: Asset): void => {
    selectedAssetIds.value.push(asset.id)
  }

  const removeAssetFromSelection = (asset: Asset): void => {
    selectedAssetIds.value = selectedAssetIds.value.filter((id) => id !== asset.id)
  }

  const clearAssetSelection = (): void => {
    selectedAssetIds.value = []
  }

  const unFocusAsset = (): void => {
    focusedAssetId.value = null
  }

  const getAssetByTypeAndId = (assetType: string, assetId: number): Asset | undefined => {
    return assets.value.find((a) => a.id === assetId && a.assetType.id === assetType)
  }

  const getAssetById = async (assetId: number): Promise<Asset | undefined> => {
    if (assetsById.value[assetId]) {
      return assetsById.value[assetId]
    }

    return loadAssetById(assetId)
  }

  const addSelectedAssetIds = (ids: number[]): void => {
    const newIds = ids.filter((id) => !selectedAssetIds.value.includes(id))
    selectedAssetIds.value = [...selectedAssetIds.value, ...newIds]
  }

  const removeSelectedAssetIds = (ids: number[]): void => {
    selectedAssetIds.value = selectedAssetIds.value.filter((id) => !ids.includes(id))
  }

  const setSelectedAssetIds = (ids: number[]): void => {
    selectedAssetIds.value = [...ids]
  }

  const clearSelectedAssetIds = (): void => {
    selectedAssetIds.value = []
  }

  const setSectionRouteMap = (entries: Map<number, number>): void => {
    sectionRouteMap.value.clear()
    sectionRouteMap.value = new Map(entries)
  }

  // Reset function for testing
  const $reset = (): void => {
    isLoading.value = false
    loadedTypes.value = []
    assets.value = []
    assetsById.value = {}
    assetsCount.value = []
    focusedAssetId.value = null
    selectedAssetIds.value = []
    stacksGrouping.value = 'site'
    sectionRouteMap.value.clear()
  }

  // Helpers - not exposed
  const appendToDictionary = (assetsToAppend: Asset[]): void => {
    /** The dictionary is only used for quick access to assets by ID
     * So we must ensure it does not grow indefinitely. We could implement a LRU
     * or other caching strategy, but for now we will just limit or wipe.
     **/
    if (Object.keys(assetsById.value).length === 25) {
      assetsById.value = {}
    }

    assetsById.value = assetsToAppend.reduce((dictionary, asset) => {
      return {
        ...dictionary,
        [asset.id]: asset
      }
    }, assetsById.value)
  }

  return {
    // State
    isLoading,
    loadedTypes,
    assets,
    assetsById,
    focusedAssetId,
    selectedAssetIds,
    networkStatus,
    assetsCount,
    currentFilters,
    pagination,
    searchTerm,
    stacksGrouping,
    sectionRouteMap,

    // Getters
    assetsIds,

    // Actions
    loadFilteredAssets,
    loadAssetById,
    updateAsset,
    assignGroupsToAsset,
    loadGroupedAssetsCount,
    loadNetworkStatuses,
    focusAsset,
    addAssetToSelection,
    removeAssetFromSelection,
    clearAssetSelection,
    unFocusAsset,
    getAssetByTypeAndId,
    getAssetById,
    addSelectedAssetIds,
    removeSelectedAssetIds,
    setSelectedAssetIds,
    clearSelectedAssetIds,
    setSectionRouteMap,

    $reset
  }
})
